import type { BankAccount, VerifiedSlip } from '@pf/contracts';
import { ArrowRight, Check, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { bankInfo, isSameBank, maskedAccountMatches } from '@/lib/banks';
import { cn } from '@/lib/utils';
import { CopyButton } from './copy-button';

/**
 * Expected payee versus what the bank actually attested — field by field.
 *
 * This is the core of the Manual Review screen. The operator's job on a review case is to decide
 * whether the money reached *this* withdrawer, and that decision is made by comparing four
 * things. Putting them side by side with the differing ones marked turns a careful reading task
 * into a scanning task, which is the difference between a 30-second review and a 3-minute one.
 *
 * The name comparison deliberately shows both the raw strings *and* the normalised forms, because
 * the SAD §6 matcher scores the normalised pair — showing only the raw strings would make a
 * perfectly good 0.94 match look like a mismatch to a human ("นาย สมชาย ใจดี" vs "สมชาย ใจดี").
 */

/** Props for {@link PayeeDiff}. */
export interface PayeeDiffProps {
  /** The reserved withdrawer's account — what PaymentFlow expected to be paid. */
  expected: BankAccount;
  /** What the provider returned, or `null` when no slip was ever verified. */
  actual: VerifiedSlip | null;
  /** Fuzzy name score from the matcher, when known. */
  nameScore?: number | null | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** Strip Thai and Latin honorifics, collapse whitespace, case-fold — mirrors SAD §6. */
export function normalizeName(name: string): string {
  return name
    .replace(/^(นาย|นางสาว|นาง|ด\.?ช\.?|ด\.?ญ\.?)\s*/u, '')
    .replace(/^(mr|mrs|ms|miss|mme|dr)\.?\s+/iu, '')
    .replace(/\s+/gu, ' ')
    .trim()
    .toLocaleLowerCase();
}

interface Row {
  label: string;
  expected: string;
  actual: string | null;
  matches: boolean | null;
  note?: string | undefined;
  /** Whether the values in this row get a copy button beside them. */
  copyable?: boolean | undefined;
}

/** Render the expected-vs-actual payee comparison. */
export function PayeeDiff({ expected, actual, nameScore, className }: PayeeDiffProps): ReactNode {
  const { t } = useTranslation();
  const expectedBank = bankInfo(expected.bank);
  const actualAccount = actual?.receiver.account_masked;

  const rows: Row[] = [
    {
      label: t('domain.payeeDiff.accountName'),
      expected: expected.name,
      actual: actual?.receiver.name ?? null,
      matches: actual ? normalizeName(expected.name) === normalizeName(actual.receiver.name) : null,
      note: actual
        ? nameScore !== null && nameScore !== undefined
          ? t('domain.payeeDiff.nameNoteWithScore', {
              expectedName: normalizeName(expected.name),
              actualName: normalizeName(actual.receiver.name),
              score: nameScore.toFixed(2),
            })
          : t('domain.payeeDiff.nameNote', {
              expectedName: normalizeName(expected.name),
              actualName: normalizeName(actual.receiver.name),
            })
        : undefined,
      copyable: true,
    },
    {
      label: t('domain.payeeDiff.bank'),
      expected: `${expectedBank.name} (${expected.bank})`,
      actual: actual ? `${bankInfo(actual.receiver.bank).name} (${actual.receiver.bank})` : null,
      matches: actual ? isSameBank(expected.bank, actual.receiver.bank) : null,
    },
    {
      label: t('domain.payeeDiff.accountNumber'),
      // As-is, not re-masked: the API now sends the full number precisely because every bank
      // masks a different span, so a masked "expected" cannot be lined up against the slip's
      // differently-masked "actual" (live case: xxxxxx9010 vs xxx-x-x2901-x, same account).
      expected: expected.number,
      actual: actualAccount ?? null,
      // SAD §6: the account is "often masked/omitted → match last-4 when present, don't
      // require". An omitted account is `null` (unknown), never `false` (mismatch) — the
      // contract itself makes this field optional for exactly this reason. Comparison is
      // positional against the mask, because each bank masks a different span of the number.
      matches:
        actualAccount === undefined ? null : maskedAccountMatches(actualAccount, expected.number),
      note: actual ? t('domain.payeeDiff.accountNumberNote') : undefined,
      copyable: true,
    },
    {
      label: t('domain.payeeDiff.amountUnused'),
      expected: '—',
      actual: actual ? `${actual.amount.value} ${actual.amount.currency}` : null,
      matches: null,
    },
  ].filter((row) => row.label !== t('domain.payeeDiff.amountUnused'));

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border', className)}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 border-b border-border bg-muted/50 px-4 py-2 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>{t('domain.payeeDiff.expectedPayeeHeader')}</span>
        <span className="sr-only">{t('domain.payeeDiff.comparedWith')}</span>
        <span aria-hidden="true" />
        <span>{t('domain.payeeDiff.attestedPayeeHeader')}</span>
      </div>

      {/*
        No verified slip is not "nothing to show": the expected side (the reserved withdrawer's
        account) is exactly what the operator needs on screen to eyeball against the customer's
        uploaded slip image, so the rows render regardless and only the actual column degrades.
      */}
      <ul className="divide-y divide-border">
        {rows.map((row) => (
          <li
            key={row.label}
            className="grid grid-cols-1 gap-x-3 gap-y-1 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center"
            data-field={row.label}
            data-matches={row.matches === null ? 'unknown' : String(row.matches)}
          >
            <div className="min-w-0">
              <p className="text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                {row.label}
              </p>
              <p className="flex items-center gap-1 break-words text-sm text-foreground">
                <span className="min-w-0 break-words">{row.expected}</span>
                {row.copyable === true ? (
                  <CopyButton
                    value={row.expected}
                    label={row.label}
                    className="size-5 shrink-0 [&_svg]:size-3"
                  />
                ) : null}
              </p>
            </div>

            <div className="hidden items-center justify-center sm:flex">
              {row.matches === true ? (
                <span
                  className="flex size-6 items-center justify-center rounded-full bg-state-good-bg text-state-good"
                  title={t('domain.payeeDiff.matchesTitle')}
                >
                  <Check className="size-3.5" aria-hidden="true" />
                  <span className="sr-only">{t('domain.payeeDiff.matchesSr')}</span>
                </span>
              ) : row.matches === false ? (
                <span
                  className="flex size-6 items-center justify-center rounded-full bg-state-bad-bg text-state-bad"
                  title={t('domain.payeeDiff.doesNotMatchTitle')}
                >
                  <X className="size-3.5" aria-hidden="true" />
                  <span className="sr-only">{t('domain.payeeDiff.doesNotMatchSr')}</span>
                </span>
              ) : (
                <ArrowRight className="size-4 text-muted-foreground" aria-hidden="true" />
              )}
            </div>

            <div className="min-w-0">
              <p className="sr-only">{t('domain.payeeDiff.fieldOnSlipSr', { label: row.label })}</p>
              <p
                className={cn(
                  'flex items-center gap-1 break-words text-sm',
                  row.matches === false
                    ? 'font-semibold text-state-bad'
                    : row.actual === null
                      ? 'text-muted-foreground'
                      : 'text-foreground',
                )}
              >
                <span className="min-w-0 break-words">
                  {row.actual ??
                    t(
                      actual === null
                        ? 'domain.payeeDiff.noSlipCell'
                        : 'domain.payeeDiff.notProvided',
                    )}
                </span>
                {/* Never over a placeholder — "not provided" is not worth a clipboard slot. */}
                {row.copyable === true && row.actual !== null ? (
                  <CopyButton
                    value={row.actual}
                    label={t('domain.payeeDiff.fieldOnSlipSr', { label: row.label })}
                    className="size-5 shrink-0 [&_svg]:size-3"
                  />
                ) : null}
              </p>
              {row.note ? (
                <p className="pt-1 text-2xs leading-relaxed text-muted-foreground">{row.note}</p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {actual === null ? (
        <p className="border-t border-border bg-muted/30 px-4 py-3 text-2xs leading-relaxed text-muted-foreground">
          {t('domain.payeeDiff.noSlipMessage')}
        </p>
      ) : null}
    </div>
  );
}
